import { useMemo } from "react";
import { JSX } from "react/jsx-runtime";
import { ChevronIcon } from "../../materials/icons/chevron-icon/ChevronIcon";
import { Body } from "../body/Body";
import { IconButton } from "../icon-button/IconButton.Parts";
import {
  Container,
  PageButton,
  PageButtonsContainer,
  Props,
  getFooterText,
  getPaginationRange,
  text,
} from "./Pagination.Parts";

export const Pagination = ({
  displayLimit,
  pageSize,
  totalRecords,
  onPageClick,
  disabled,
  footer = true,
  pageIndex,
  hideSinglePage = false,
  alwaysShowFirstPageNumber = false,
  alwaysShowLastNumber = false,
  showFirstThreeDotsOnLastPageOnly,
}: Props) => {
  /** Simple calculation to get total pages */
  const totalPages = Math.ceil(totalRecords / pageSize);
  const limit = Math.min(totalPages, displayLimit ?? 0);
  const lastPage = totalPages - 1;
  /** Sets the first and last clickable page indexes from getPaginationRange's results */
  const [startIndex, endIndex] = getPaginationRange({
    limit,
    pageIndex,
    totalPages,
  });

  const pageIcons = useMemo(() => {
    const icons: JSX.Element[] = [];
    for (let i = startIndex; i < endIndex; i++) {
      icons.push(
        <PageButton
          disabled={disabled}
          $selected={i === pageIndex}
          key={i}
          tabIndex={i === pageIndex ? -1 : 0}
          onClick={() => onPageClick(i)}
          aria-label={
            i === pageIndex
              ? `${text.page} ${i + 1}, ${text.currentPage}`
              : `${text.gotoPage} ${i + 1}`
          }
          aria-current={i === pageIndex ? "page" : undefined}
        >
          {/* Adds one to the display number since page indexes are zero based */}
          {i + 1}
        </PageButton>,
      );
    }
    return icons;
  }, [startIndex, endIndex, disabled, pageIndex, onPageClick]);

  const showButtons = hideSinglePage ? totalPages > 1 : true;

  return (
    <Container>
      {showButtons && (
        <PageButtonsContainer aria-label={text.container}>
          <IconButton
            disabled={disabled || pageIndex === 0 || pageIndex < 0}
            onClick={() => onPageClick(pageIndex - 1)}
            aria-label={text.gotoPreviousPage}
          >
            <ChevronIcon orientation="left" aria-hidden="true" />
          </IconButton>
          {alwaysShowFirstPageNumber && startIndex > 0 && (
            <PageButton
              disabled={disabled}
              $selected={pageIndex === 0}
              tabIndex={0 === pageIndex ? -1 : 0}
              onClick={() => onPageClick(0)}
              aria-label={
                0 === pageIndex
                  ? `${text.page} 1, ${text.currentPage}`
                  : `${text.page} 1`
              }
              aria-current={0 === pageIndex ? "page" : undefined}
            >
              1
            </PageButton>
          )}
          {((startIndex > 0 && !showFirstThreeDotsOnLastPageOnly) ||
            (showFirstThreeDotsOnLastPageOnly && lastPage === pageIndex)) && (
            <span aria-label={text.morePagesAriaLabel} role="presentation">
              …
            </span>
          )}
          {pageIcons}
          {endIndex < totalPages && (
            <span aria-label={text.morePagesAriaLabel} role="presentation">
              …
            </span>
          )}
          {alwaysShowLastNumber && endIndex <= lastPage && (
            <PageButton
              disabled={disabled}
              $selected={pageIndex === lastPage}
              tabIndex={lastPage === pageIndex ? -1 : 0}
              onClick={() => onPageClick(lastPage)}
              aria-label={
                lastPage === pageIndex
                  ? `${text.page} ${lastPage}, ${text.currentPage}`
                  : `${text.gotoPage} ${lastPage}`
              }
              aria-current={lastPage === pageIndex ? "page" : undefined}
            >
              {lastPage + 1}
            </PageButton>
          )}
          <IconButton
            disabled={
              disabled ||
              pageIndex === totalPages - 1 ||
              pageIndex > totalPages - 1
            }
            onClick={() => onPageClick(pageIndex + 1)}
            aria-label={text.gotoNextPage}
          >
            <ChevronIcon orientation="right" aria-hidden="true" />
          </IconButton>
        </PageButtonsContainer>
      )}
      {footer && (
        <Body variant="xsmall">
          {getFooterText(pageIndex, pageSize, totalRecords)}
        </Body>
      )}
    </Container>
  );
};
